import { useState, useRef } from 'react';
import type React from 'react';
import type { MutableRefObject } from 'react';
import classNames from 'classnames';
import { twMerge } from 'tailwind-merge';

import Button from 'components/partials/button/button';
import Tooltip from 'components/partials/tooltip/tooltip';
import FeedbackSection from './feedback-section';
import MessageParts from './message-parts';
import CopiedBadge from './copied-badge';
import CopyAnnouncer from './copy-announcer';

import { getTestId } from 'helpers/test-helpers';

import { useApiSendFeedback } from '../../hooks/use-api-send-feedback';
import { useMessageCopy } from '../../hooks/use-message-copy';

import { resolveMessageParts } from '../../helpers/message-helpers';

import {
  AI_ASSISTANT_CHAT_MESSAGE_MAX_WIDTH_CLASS,
  FEEDBACK_TYPES,
  INLINE_CONFIRMATION_FADE_MS,
  MESSAGE_ACTION_BUTTON_CLASS,
} from '../../ai-assistant.constants';

import type { ChatMessage } from 'containers/admin/clients/client/tabs-components/ai-assistant/hooks/use-active-input-part';

import Icons from 'assets/icons';

export interface AiMessageItemProps {
  message: ChatMessage & { content?: string };
  oktaUserId: string;
  clientId: string | number;
  conversationId: string;
  feedbackCloseRef: MutableRefObject<(() => void) | null>;
  onRegenerateMessage?: (messageId: string) => void;
  isReadOnly?: boolean;
  isRegenerateDisabled?: boolean;
}

interface MessageAction {
  icon: React.JSX.Element;
  label: string;
  onClick: () => void;
  isDisabled: boolean;
  customClass?: string;
}

const AiMessageItem = ({
  message,
  oktaUserId,
  clientId,
  conversationId,
  feedbackCloseRef,
  onRegenerateMessage,
  isReadOnly,
  isRegenerateDisabled,
}: AiMessageItemProps): React.JSX.Element => {
  const actionsRef = useRef<HTMLDivElement>(null);
  const messageRootRef = useRef<HTMLDivElement>(null);

  const [isLiked, setIsLiked] = useState(false);
  const [isDisliked, setIsDisliked] = useState(false);
  const [feedbackType, setFeedbackType] = useState<string | null>(null);
  const [isFeedbackSectionOpen, setIsFeedbackSectionOpen] = useState(false);

  const { copyConfirmationPhase, copyAnnouncement, copyContent } =
    useMessageCopy(message.content ?? '');

  const { mutateAsync: sendFeedback, isPending: isSendingFeedback } =
    useApiSendFeedback({
      oktaUserId,
      clientId,
      conversationId,
      messageId: message.id,
    });

  const areActionsDisabled = isSendingFeedback || !!isReadOnly;

  const handleRegenerateMessage = (): void => {
    onRegenerateMessage?.(message.id);
  };

  const handleCloseFeedbackSection = (): void => {
    setFeedbackType(null);
    setIsFeedbackSectionOpen(false);
    if (feedbackCloseRef) feedbackCloseRef.current = null;
  };

  const handleSendFeedback = async (
    type: string | null,
    reason = ''
  ): Promise<void> => {
    await sendFeedback({ type, reason });

    if (type === FEEDBACK_TYPES.POSITIVE) {
      setIsLiked(true);
    } else if (type === FEEDBACK_TYPES.NEGATIVE) {
      setIsDisliked(true);
    }

    handleCloseFeedbackSection();
  };

  const handleOpenFeedbackSection = (type: string): void => {
    feedbackCloseRef?.current?.();
    feedbackCloseRef.current = () => {
      setIsFeedbackSectionOpen(false);
      feedbackCloseRef.current = null;
    };

    setFeedbackType(type);
    setIsFeedbackSectionOpen(true);
  };

  const handleCancelFeedback = (): void => {
    setFeedbackType(null);
    setIsLiked(false);
    setIsDisliked(false);
  };

  const actions: MessageAction[] = (
    [
      {
        icon: <Icons.Retry />,
        label: 'Retry',
        onClick: handleRegenerateMessage,
        isDisabled: areActionsDisabled || !!isRegenerateDisabled,
      },
      {
        icon: <Icons.Copy />,
        label: 'Copy',
        onClick: () => {
          void copyContent();
        },
        isDisabled: areActionsDisabled,
      },
      !isDisliked && {
        icon: isLiked ? <Icons.ThumbsUpFilled /> : <Icons.ThumbsUp />,
        label: isLiked ? 'Liked' : 'Like',
        onClick: isLiked
          ? handleCancelFeedback
          : () => handleOpenFeedbackSection(FEEDBACK_TYPES.POSITIVE),
        isDisabled: areActionsDisabled,
      },
      !isLiked && {
        icon: isDisliked ? <Icons.ThumbsDownFilled /> : <Icons.ThumbsDown />,
        label: isDisliked ? 'Disliked' : 'Dislike',
        onClick: isDisliked
          ? handleCancelFeedback
          : () => handleOpenFeedbackSection(FEEDBACK_TYPES.NEGATIVE),
        isDisabled: areActionsDisabled,
      },
    ] as Array<MessageAction | false>
  ).filter(Boolean) as MessageAction[];

  // Crossfade: hide actions while badge is fully shown; fade back in during `leaving`.
  const areActionsVisible =
    copyConfirmationPhase === 'idle' || copyConfirmationPhase === 'leaving';

  return (
    <div
      ref={messageRootRef}
      className={twMerge(
        'relative animate-fade-in flex flex-col gap-2 text-md text-gray-900 bg-transparent mr-auto w-full min-w-0',
        AI_ASSISTANT_CHAT_MESSAGE_MAX_WIDTH_CLASS
      )}
    >
      <div className="flex flex-col gap-2 min-w-0 w-full">
        <MessageParts
          messageId={message.id}
          parts={resolveMessageParts(message)}
        />
      </div>

      <div ref={actionsRef} className="relative flex h-8 w-fit items-center">
        <CopyAnnouncer message={copyAnnouncement} />
        {copyConfirmationPhase !== 'idle' && (
          <div className="absolute inset-0 flex items-center">
            <CopiedBadge phase={copyConfirmationPhase} />
          </div>
        )}
        <div
          data-testid="message-ai-actions"
          className={classNames(
            'flex items-center transition-opacity',
            areActionsVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
          )}
          style={{ transitionDuration: `${INLINE_CONFIRMATION_FADE_MS}ms` }}
        >
          {actions.map((action) => {
            const dataTestId = getTestId({
              prefix: 'message-action',
              label: action.label,
              suffix: 'button',
            });

            return (
              <Tooltip
                key={action.label}
                content={action.label}
                position="bottom-start"
                enabled={!action.isDisabled}
              >
                <Button
                  variant="reserve-text"
                  className={classNames(
                    MESSAGE_ACTION_BUTTON_CLASS,
                    action.customClass
                  )}
                  onClick={action.onClick}
                  disabled={action.isDisabled}
                  aria-label={action.label}
                  data-testid={dataTestId}
                >
                  {action.icon}
                </Button>
              </Tooltip>
            );
          })}
        </div>
      </div>

      {isFeedbackSectionOpen && (
        <FeedbackSection
          onClose={handleCloseFeedbackSection}
          onSendFeedback={handleSendFeedback}
          isSendingFeedback={isSendingFeedback}
          feedbackType={feedbackType}
          anchorRef={messageRootRef}
          triggerRef={actionsRef}
        />
      )}
    </div>
  );
};

export default AiMessageItem;
